import { useEffect, useRef } from "react";

// Embeds the AI Visibility Audit tool (audit.seoptimist.co.uk/widget.js).
//  - default: light theme, inline (the standalone /ai-visibility page)
//  - takeover: dark theme + data-mode=takeover (compact trigger -> full-screen panel on run)
//  - takeover + showHeading: also show the widget's own heading in the compact hero
//    (the homepage; the eyebrow is removed and the H1 copy is overridden via data-heading)
// IMPORTANT: CSS attribute selectors here are UNQUOTED ([data-mode=takeover], not
// [data-mode="takeover"]). Next.js HTML-encodes the " inside <style> to &quot;, which
// the browser drops as an invalid selector. Unquoted values are valid CSS and safe.
type Props = { takeover?: boolean; showHeading?: boolean };

const GeoAudit = ({ takeover = false, showHeading = false }: Props) => {
  const loaded = useRef(false);
  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    const s = document.createElement("script");
    s.src = "https://audit.seoptimist.co.uk/widget.js";
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const baseAttrs = {
    "data-logo": "https://seoptimist.co.uk/seoptimist-black.png",
    "data-cta-email": "james@seoptimist.co.uk",
    "data-cta-url": "seoptimist.co.uk",
    "data-cta-text": "Get in touch to start appearing in AI answers",
    "data-email-capture": "",   // D-018: end-of-report shows the email-me-the-report form (approval-gated)
  };

  return (
    <>
      <style>{`
        /* light theme - standalone /ai-visibility */
        .geo-embed-wrap .geo-audit {
          --geo-font: inherit; --geo-display: inherit;
          --geo-bg:#ffffff; --geo-surface:#f7f9fc; --geo-surface-solid:#f7f9fc;
          --geo-ink:#343f52; --geo-ink-soft:#6b7480; --geo-line:#e3e7ee;
          --geo-accent:#3f78e0; --geo-accent-ink:#ffffff;
          --geo-positive:#45c4a0; --geo-alert:#e0533f; --geo-highlight:#dbe6fb;
          --geo-neutral:#c3c9d4; --geo-link:#3f78e0; --geo-radius:14px; --geo-btn-radius:10px;
        }
        .geo-embed-wrap { max-width:960px; margin-left:auto; margin-right:auto; }

        /* dark theme - hero takeover (scoped to the element so it survives the portal-to-body) */
        .geo-audit[data-mode=takeover] {
          --geo-font: inherit; --geo-display: inherit;
          --geo-bg:#101216; --geo-surface:#191c22; --geo-surface-solid:#191c22;
          --geo-ink:#e6e9ee; --geo-ink-soft:#aab2bd; --geo-line:#2e333c;
          --geo-accent:#3f78e0; --geo-accent-ink:#f4f6f9;
          --geo-positive:#45c4a0; --geo-alert:#e0735f; --geo-highlight:rgba(63,120,224,.22);
          --geo-neutral:#5b6470; --geo-link:#5eb9f0; --geo-radius:12px; --geo-btn-radius:8px;
        }
        /* D-017 brightness hierarchy (seoptimist dark, host-side only): headings brightest, body a step
           down (both off-white, never pure white), secondary = --geo-ink-soft. */
        .geo-audit[data-mode=takeover] h1,
        .geo-audit[data-mode=takeover] h2,
        .geo-audit[data-mode=takeover] h3,
        .geo-audit[data-mode=takeover] h4 { color:#f4f6f9; }

        /* SIZING: mirror the site's Bootstrap .form-control + .btn (field .75rem, padding .6rem 1rem) */
        .geo-embed-wrap .geo-audit input, .geo-embed-wrap .geo-audit textarea, .geo-embed-wrap .geo-audit select,
        .geo-audit[data-mode=takeover] input, .geo-audit[data-mode=takeover] textarea, .geo-audit[data-mode=takeover] select {
          padding:.6rem 1rem; font-size:.75rem; line-height:1.7; border-radius:.4rem;
        }
        .geo-embed-wrap .geo-audit button, .geo-audit[data-mode=takeover] button {
          padding:.5rem 1.2rem; font-size:.8rem; border-radius:.4rem;
        }
        .geo-embed-wrap .geo-audit label, .geo-audit[data-mode=takeover] label { font-size:.7rem; }

        /* dark-only colours (widget reuses --geo-ink for button bg + translucent white for inputs) */
        .geo-audit[data-mode=takeover] button { background:var(--geo-accent); }
        .geo-audit[data-mode=takeover] button:hover:not(:disabled) { background:#5b8de8; }
        .geo-audit[data-mode=takeover] input,
        .geo-audit[data-mode=takeover] textarea,
        .geo-audit[data-mode=takeover] select { background:rgba(255,255,255,.06); }

        /* COMPACT HERO (takeover, before expand): no outer panel, no card bubble - just heading + bare field */
        .geo-embed-dark .geo-audit[data-mode=takeover]:not(.geo-takeover-open) { background:transparent !important; border-radius:0; }
        .geo-embed-dark .geo-audit[data-mode=takeover]:not(.geo-takeover-open)::before { display:none; }
        .geo-embed-dark .geo-audit[data-mode=takeover]:not(.geo-takeover-open) .wrap { padding:0; }
        .geo-embed-dark .geo-audit[data-mode=takeover]:not(.geo-takeover-open) .card[data-key=domain] {
          background:transparent; border:none; box-shadow:none; padding:0; margin:1.4rem auto 0; max-width:500px;
        }
        .geo-embed-dark .geo-audit[data-mode=takeover]:not(.geo-takeover-open) .card[data-key=domain] label { display:none; }
        /* show the widget's own heading/intro in the compact hero (replaces the site H1) */
        .geo-audit.geo-hero-head[data-mode=takeover]:not(.geo-takeover-open) h1,
        .geo-audit.geo-hero-head[data-mode=takeover]:not(.geo-takeover-open) .sub { display:block; text-align:center; }

        /* DOMAIN FIELD: input grows, small Check button to the right; stack on mobile */
        .geo-audit[data-mode=takeover] .card[data-key=domain] .row { display:flex; flex-wrap:nowrap; align-items:stretch; gap:10px; width:100%; max-width:100%; }
        .geo-audit[data-mode=takeover] .card[data-key=domain] .row input { flex:1 1 0; width:auto; min-width:0; }
        .geo-audit[data-mode=takeover] .card[data-key=domain] .row button { flex:0 0 auto; width:auto; white-space:nowrap; }
        @media (max-width:560px){
          .geo-audit[data-mode=takeover] .card[data-key=domain] .row { flex-direction:column; }
          /* 2026-10-22: Bootstrap's .row has -15px side margins, which pushed the field 15px past the card and the card's overflow:hidden cut the left edge off. On phones the row now fills the card exactly, and the field and button are about a third smaller. */
          .geo-audit[data-mode=takeover] .card[data-key=domain] .row { margin-left:0; margin-right:0; }
          .geo-audit[data-mode=takeover] .card[data-key=domain] .row input { padding:.4rem .8rem; font-size:.7rem; line-height:1.5; }
          .geo-audit[data-mode=takeover] .card[data-key=domain] .row button { padding:.45rem 1rem; font-size:.72rem; }
          .geo-audit[data-mode=takeover] .card[data-key=domain] .row button { width:100%; }
        }

        .geo-embed-dark { max-width:700px; margin-left:auto; margin-right:auto; }

        @media print {
          .geo-embed-heading { display:none !important; }
          .geo-embed-section { padding:0 !important; }
          .geo-embed-wrap, .geo-embed-dark { max-width:none !important; margin:0 !important; }
          .geo-audit[data-mode=takeover] {
            --geo-bg:#ffffff; --geo-surface:#f7f9fc; --geo-surface-solid:#f7f9fc;
            --geo-ink:#343f52; --geo-ink-soft:#6b7480; --geo-line:#e3e7ee; --geo-highlight:#dbe6fb;
          }
          .geo-audit[data-mode=takeover] button { background:var(--geo-ink); }
          .geo-audit[data-mode=takeover] input { background:#fff; }
        }
      `}</style>
      <div className={takeover ? "geo-embed-dark" : "geo-embed-wrap"}>
        {takeover
          ? <div className={"geo-audit" + (showHeading ? " geo-hero-head" : "")} data-mode="takeover"
              data-eyebrow="" data-heading="Are you in AI answers?" {...baseAttrs} />
          : <div className="geo-audit" data-scroll-offset="100" {...baseAttrs} />}
      </div>
    </>
  );
};

export default GeoAudit;
